// LiveSwitch -> RemoteAudioFeed adapter. The only LiveSwitch-aware file in the
// build besides types.ts.
//
// Spec s4/s12: this opens NO LiveSwitch connections. The existing app already
// creates a downstream per remote upstream and already has the remote audio
// arriving. All we do is take a reference to the MediaStreamTrack it produced,
// which adds zero connection-minutes.
//
// Integration: call `register` from wherever the host app's downstream open
// handler runs, and `unregister` from its close handler. Those two call sites
// are the entire wiring.

import type { RemoteAudioFeed, RemoteAudioTrack } from '../audio/RemoteAudioFeed';
import { audioTrackOf } from './media';
import type { ChannelLike, RemoteMediaLike } from './types';

/**
 * Pull the underlying MediaStreamTrack out of a LiveSwitch RemoteMedia.
 *
 * Delegates to media.ts, which reads the native stream off the SDK's internal
 * WebRtcMedia. The view is NOT the route: the SDK's audio element is created
 * privately and never appears in getView(), so searching the view for an
 * <audio> element finds nothing while the audio plays perfectly.
 */
export function extractRemoteAudioTrack(remoteMedia: RemoteMediaLike): MediaStreamTrack | null {
  return audioTrackOf(remoteMedia);
}

export interface LiveSwitchFeed extends RemoteAudioFeed {
  /** Call from the host app's existing downstream-open handler. */
  register(clientId: string, remoteMedia: RemoteMediaLike): void;
  /**
   * Add an audio track directly, for audio that does not arrive as a LiveSwitch
   * RemoteMedia - our own microphone, most of all.
   */
  registerTrack(id: string, track: MediaStreamTrack): void;
  /** Call from the host app's existing downstream-close handler. */
  unregister(clientId: string): void;
  /** Optional: auto-unregister on channel leave events. */
  attachChannel(channel: ChannelLike): () => void;
  dispose(): void;
}

export function createLiveSwitchFeed(): LiveSwitchFeed {
  const tracks = new Map<string, MediaStreamTrack>();
  const addedHandlers = new Set<(entry: RemoteAudioTrack) => void>();
  const removedHandlers = new Set<(id: string) => void>();

  const registerTrack = (id: string, track: MediaStreamTrack) => {
    // Re-registering the same id replaces the track, which is what a microphone
    // change looks like from here.
    if (tracks.get(id) === track) return;
    if (tracks.has(id)) unregister(id);
    tracks.set(id, track);
    // A track that ends on its own (renegotiation, mute-to-stop) leaves too.
    track.addEventListener('ended', () => unregister(id), { once: true });
    for (const handler of addedHandlers) handler({ id, track });
  };

  const unregister = (clientId: string) => {
    if (!tracks.delete(clientId)) return;
    for (const handler of removedHandlers) handler(clientId);
  };

  return {
    register(clientId, remoteMedia) {
      const track = extractRemoteAudioTrack(remoteMedia);
      if (!track) {
        console.warn(`[liveswitch] no audio track on remote media for ${clientId}`);
        return;
      }
      registerTrack(clientId, track);
    },

    registerTrack,

    unregister,

    attachChannel(channel) {
      const onLeave = (info: { getId?(): string }) => {
        const id = info?.getId?.();
        if (id) unregister(id);
      };
      channel.addOnRemoteClientLeave?.(onLeave);
      return () => channel.removeOnRemoteClientLeave?.(onLeave);
    },

    current: () => [...tracks].map(([id, track]) => ({ id, track })),

    onAdded(handler) {
      addedHandlers.add(handler);
      return () => addedHandlers.delete(handler);
    },

    onRemoved(handler) {
      removedHandlers.add(handler);
      return () => removedHandlers.delete(handler);
    },

    dispose() {
      tracks.clear();
      addedHandlers.clear();
      removedHandlers.clear();
    },
  };
}
